import React from 'react';

import { Box, Typography } from '@mui/material';
import PdfCard from 'app/shared/components/document/DocumentView/PdfCard';
import AuditStateMessage from 'app/shared/modules/EntityAuditHistory/components/AuditStateMessage';
import { DETAIL_RETRY_LABEL } from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.constants';
import PropTypes from 'prop-types';

import useContentWidth from 'hooks/useContentWidth';

import { PREVIEW_STATE_MESSAGE } from '../../FormChangeHistory.constants';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import { getMissingPageMessage } from '../../FormChangeHistory.utils';
import PreviewMarkBadge from '../PreviewMarkBadge';

import {
  getPageMarkBadgeStyles,
  getPageMarkStyles,
  pageFrameStyles,
  pageMarkFrameStyles,
  previewPaneStyles,
} from './PreviewPagePane.styles';
import { getStackedBadges } from './PreviewPagePane.utils';

/**
 * One side of a preview at one page, as wide as the pane: the page with its changes numbered and
 * the highlighted one outlined, or why there is no page to show.
 */
const PreviewPagePane = ({
  side,
  title,
  status,
  pageNumber,
  onRetry,
  document = null,
  marks = [],
  highlightedMarkId = null,
}) => {
  const { contentWidth, setRef } = useContentWidth();

  const stateMessage = PREVIEW_STATE_MESSAGE[status] ?? null;
  const pageSize = document?.pageSizes[pageNumber - 1] ?? null;
  const pageHeight = pageSize ? (contentWidth * pageSize.height) / pageSize.width : 0;
  const highlightedMarks = marks.filter(mark => mark.id === highlightedMarkId);

  return (
    <Box
      data-side={side}
      data-status={status}
      data-testid={FormChangeHistoryTestIds.PREVIEW_PANE}
      sx={previewPaneStyles}
    >
      <Typography variant="medium14">{title}</Typography>

      {stateMessage && (
        <AuditStateMessage
          dataTestId={FormChangeHistoryTestIds.PREVIEW_STATE}
          isLoading={stateMessage.isLoading}
          message={stateMessage.message}
          retryLabel={DETAIL_RETRY_LABEL}
          retryTestId={FormChangeHistoryTestIds.PREVIEW_RETRY_BUTTON}
          onRetry={stateMessage.hasRetry ? onRetry : null}
        />
      )}

      {!stateMessage && !pageSize && (
        <AuditStateMessage
          dataTestId={FormChangeHistoryTestIds.PREVIEW_STATE}
          message={getMissingPageMessage(pageNumber)}
        />
      )}

      {pageSize && (
        <Box data-page={pageNumber} ref={setRef} sx={pageFrameStyles}>
          {contentWidth > 0 && (
            <PdfCard
              num={pageNumber}
              pageSize={pageSize}
              pdf={document.pdf}
              scale={contentWidth / pageSize.width}
            />
          )}
          {highlightedMarks.map(mark => (
            <Box
              data-number={mark.number}
              data-testid={FormChangeHistoryTestIds.PREVIEW_PAGE_MARK}
              key={mark.id}
              sx={getPageMarkStyles(mark)}
            >
              <Box sx={pageMarkFrameStyles} />
            </Box>
          ))}
          {getStackedBadges(marks, pageHeight).map(badge => (
            <Box key={badge.id} sx={getPageMarkBadgeStyles(badge)}>
              <PreviewMarkBadge number={badge.number} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

PreviewPagePane.propTypes = {
  side: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  pageNumber: PropTypes.number.isRequired,
  onRetry: PropTypes.func.isRequired,
  document: PropTypes.shape({
    pdf: PropTypes.object.isRequired,
    pageSizes: PropTypes.arrayOf(
      PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
    ).isRequired,
  }),
  marks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      number: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      top: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  ),
  highlightedMarkId: PropTypes.string,
};

export default PreviewPagePane;
